import React from 'react';

interface StandardSectionProps {
  content: string;
}

const StandardSection: React.FC<StandardSectionProps> = ({content}) => {
  // Helper function to render bold text
  const renderBoldText = (text: string): React.ReactNode[] => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={idx} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  // Helper function to render text with markdown formatting (bold and links)
  const renderFormattedText = (text: string) => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    // Regex to match markdown links: [text](url) or [**bold text**](url)
    const linkRegex = /\[(\*\*.*?\*\*|[^\]]+)\]\(([^)]+)\)/g;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Type guard: ensure match groups exist
      if (!match[1] || !match[2] || match.index === undefined) {
        continue;
      }

      // Add text before the link
      if (match.index > currentIndex) {
        const beforeText = text.substring(currentIndex, match.index);
        parts.push(...renderBoldText(beforeText));
      }

      // Parse the link text (may contain bold)
      const linkText = match[1];
      const linkUrl = match[2];
      const linkTextParts = renderBoldText(linkText);

      // Create the link element
      parts.push(
        <a
          key={`link-${match.index}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-pink hover:underline">
          {linkTextParts}
        </a>
      );

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text after the last link
    if (currentIndex < text.length) {
      const remainingText = text.substring(currentIndex);
      parts.push(...renderBoldText(remainingText));
    }

    return parts.length > 0 ? parts : renderBoldText(text);
  };

  return (
    <div className="prose prose-sm max-w-none text-headlineGrayscale leading-relaxed">
      {content.split('\n\n').map((paragraph, idx) => {
        if (!paragraph.trim()) return null;
        return (
          <p key={idx} className="mb-4">
            {renderFormattedText(paragraph)}
          </p>
        );
      })}
    </div>
  );
};

export default StandardSection;
